/* ═══════════════════════════════════════════════════════════════
   Cloud Cache — In-memory cache with write-through to EC2 API

   Reads:  Return cached data instantly, fetch from API on first load
   Writes: Update cache immediately (optimistic), persist via API
   Result: Native-app-feel with cloud PostgreSQL persistence
   ═══════════════════════════════════════════════════════════════ */

import { getCurrentFirmId } from './firmContext';
import * as api from './apiClient';

// ─── Session health ───

export async function refreshSessionIfNeeded(): Promise<void> {
  // No session management needed — EC2 API is stateless
}

// ─── camelCase <-> snake_case conversion ───

function toSnake(str: string): string {
  return str.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());
}

function toCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function rowToSnake(obj: Record<string, any>, opts?: TableOpts): Record<string, any> {
  const row: Record<string, any> = {};
  for (const k of Object.keys(obj)) {
    if (k === 'firmId') continue;
    const v = obj[k];
    const snakeKey = toSnake(k);
    if (opts?.jsonFields?.includes(k) && v != null && typeof v !== 'string') {
      row[snakeKey] = JSON.stringify(v);
    } else {
      row[snakeKey] = v === undefined ? null : v;
    }
  }
  if (opts?.columnMap) {
    for (const [from, to] of Object.entries(opts.columnMap)) {
      if (from in row) {
        row[to] = row[from];
        delete row[from];
      }
    }
  }
  return row;
}

function rowToCamel(obj: Record<string, any>, opts?: TableOpts): Record<string, any> {
  if (opts?.columnMap) {
    const reverse = Object.fromEntries(
      Object.entries(opts.columnMap).map(([from, to]) => [to, from])
    );
    for (const [supaCol, localCol] of Object.entries(reverse)) {
      if (supaCol in obj) {
        obj[localCol] = obj[supaCol];
        delete obj[supaCol];
      }
    }
  }
  const result: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    const camelKey = toCamel(k);
    if (opts?.jsonFields?.includes(camelKey) && typeof v === 'string') {
      try {
        result[camelKey] = JSON.parse(v);
      } catch {
        result[camelKey] = v;
      }
    } else {
      result[camelKey] = v;
    }
  }
  return result;
}

// ─── Table configuration ───

export interface TableOpts {
  jsonFields?: string[];
  columnMap?: Record<string, string>;
}

// ─── In-memory cache ───

interface CacheEntry {
  rows: any[];
  fetchedAt: number;
  opts?: TableOpts;
}

const cache = new Map<string, CacheEntry>();

const changeListeners = new Set<(table: string) => void>();

// Debounced DOM-event broadcast so bulk writes (e.g. assigning a whole team
// to a project) coalesce into a single 'specforma-sync-complete' instead of
// firing per-row and thrashing page-level refresh handlers.
let notifyTimer: any = null;
const pendingTables = new Set<string>();

function notifyChange(table: string) {
  changeListeners.forEach((fn) => {
    try {
      fn(table);
    } catch {}
  });
  if (typeof window === 'undefined') return;
  pendingTables.add(table);
  if (notifyTimer) clearTimeout(notifyTimer);
  notifyTimer = setTimeout(() => {
    const tables = Array.from(pendingTables);
    pendingTables.clear();
    notifyTimer = null;
    window.dispatchEvent(new CustomEvent('specforma-sync-complete', { detail: { tables } }));
  }, 50);
}

function cacheKey(table: string, filters?: Record<string, any>): string {
  if (!filters || Object.keys(filters).length === 0) return table;
  const sorted = Object.entries(filters).sort(([a], [b]) => a.localeCompare(b));
  return `${table}:${JSON.stringify(sorted)}`;
}

// ─── Public API ───

/**
 * Fetch all rows for a table (scoped to current firm).
 * Returns cached data on hit, fetches from PostgreSQL on first load.
 */
export async function list<T = any>(table: string, opts?: TableOpts): Promise<T[]> {
  const key = cacheKey(table);
  const cached = cache.get(key);

  if (cached) {
    return cached.rows as T[];
  }

  return fetchAndCache<T>(table, key, opts);
}

async function fetchAndCache<T = any>(table: string, key: string, opts?: TableOpts): Promise<T[]> {
  const firmId = getCurrentFirmId();
  const data = await api.dbList(table, firmId);
  const rows = (data || []).map((row) => rowToCamel(row, opts) as T);
  cache.set(key, { rows, fetchedAt: Date.now(), opts });
  return rows;
}

/**
 * Fetch rows with custom equality filters.
 * Returns cached data if available, otherwise fetches from PostgreSQL.
 */
export async function query<T = any>(
  table: string,
  filters: Record<string, any>,
  opts?: TableOpts
): Promise<T[]> {
  const key = cacheKey(table, filters);
  const cached = cache.get(key);

  if (cached) {
    return cached.rows as T[];
  }

  return fetchAndCacheQuery<T>(table, filters, key, opts);
}

async function fetchAndCacheQuery<T = any>(
  table: string,
  filters: Record<string, any>,
  key: string,
  opts?: TableOpts
): Promise<T[]> {
  const firmId = getCurrentFirmId();

  // Convert camelCase filter keys to snake_case for DB
  const snakeFilters: Record<string, any> = {};
  for (const [k, v] of Object.entries(filters)) {
    snakeFilters[toSnake(k)] = v;
  }

  const data = await api.dbList(table, firmId, snakeFilters);
  const rows = (data || []).map((row) => rowToCamel(row, opts) as T);
  cache.set(key, { rows, fetchedAt: Date.now(), opts });
  return rows;
}

/**
 * Fetch rows with arbitrary col/op/val filters (range queries, IN clauses,
 * negations).  Backed by api.dbQuery, cached with a short TTL so that
 * dashboards and forecasting pages — which fire many overlapping range
 * queries on every navigation — collapse them into a single refresh.
 *
 * Why a TTL instead of "cached forever like list/query":
 *   - Range queries (date ranges) are highly variable; an unbounded cache
 *     would grow without bound.
 *   - The TTL is short enough (30s) that user-visible staleness is bounded
 *     to roughly one render cycle, but long enough that a dashboard's 12
 *     widgets all reading the same range share one network call.
 *   - Writes still invalidate the table immediately (see invalidate()),
 *     so the freshness story for edits is unchanged.
 */
const ADVANCED_QUERY_TTL_MS = 30_000;

export async function queryAdvanced<T = any>(
  table: string,
  filters: { col: string; op: string; val: any }[],
  opts?: TableOpts,
  orderBy?: string
): Promise<T[]> {
  const firmId = getCurrentFirmId();
  const key = advancedKey(table, filters, orderBy);
  const cached = cache.get(key);
  if (cached && Date.now() - cached.fetchedAt < ADVANCED_QUERY_TTL_MS) {
    return cached.rows as T[];
  }
  const data = await api.dbQuery(table, firmId, filters, orderBy);
  const rows = (data || []).map((row) => rowToCamel(row, opts) as T);
  cache.set(key, { rows, fetchedAt: Date.now(), opts });
  return rows;
}

function advancedKey(
  table: string,
  filters: { col: string; op: string; val: any }[],
  orderBy?: string
): string {
  // Stable serialization — sort filters so {col,op,val} order doesn't
  // produce different cache keys for the same logical query.
  const norm = filters
    .map((f) => ({ col: f.col, op: f.op, val: f.val }))
    .sort((a, b) => (a.col + a.op).localeCompare(b.col + b.op));
  return `${table}::adv::${JSON.stringify(norm)}::${orderBy || ''}`;
}

/**
 * Save (upsert) a record.
 * Updates cache immediately (optimistic), then persists to PostgreSQL.
 */
export async function save<T extends { id: string }>(
  table: string,
  record: T,
  opts?: TableOpts
): Promise<T> {
  const firmId = getCurrentFirmId();
  const now = new Date().toISOString();

  const toSave = {
    ...record,
    id: record.id || crypto.randomUUID(),
    createdAt: (record as any).createdAt || now,
    ...((record as any).updatedAt !== undefined ? { updatedAt: now } : {}),
  } as T;

  // Optimistic cache update
  updateCacheEntry(table, toSave, opts);
  notifyChange(table);

  // Persist to PostgreSQL via IPC
  const row = rowToSnake(toSave as any, opts);
  row.firm_id = firmId;

  try {
    await api.dbSave(table, row);
  } catch (err: any) {
    invalidate(table);
    throw new Error(`Failed to save to ${table}: ${err.message}`);
  }

  return toSave;
}

/**
 * Delete a record by ID.
 * Removes from cache immediately, then deletes from PostgreSQL.
 */
export async function remove(table: string, id: string): Promise<void> {
  removeCacheEntry(table, id);
  notifyChange(table);

  try {
    await api.dbRemove(table, id);
  } catch (err: any) {
    invalidate(table);
    throw new Error(`Failed to delete from ${table}: ${err.message}`);
  }
}

// ─── Cache manipulation helpers ───

function updateCacheEntry<T extends { id: string }>(table: string, record: T, opts?: TableOpts) {
  const now = Date.now();
  // Drop any advanced-query caches for this table — their predicates
  // (date ranges, IN clauses, negations) are too varied to patch
  // incrementally, and TTL expiry means the next read is a refresh anyway.
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(table + '::adv::')) cache.delete(key);
  }
  for (const [key, entry] of cache.entries()) {
    if (key === table || key.startsWith(table + ':')) {
      const idx = entry.rows.findIndex((r: any) => r.id === record.id);
      if (idx >= 0) {
        // Existing row — check if it still matches this filter after the update
        if (key === table) {
          entry.rows[idx] = { ...record };
        } else {
          const matches = recordMatchesCacheKey(record, table, key);
          if (matches) entry.rows[idx] = { ...record };
          else entry.rows.splice(idx, 1); // no longer matches the filter, drop it
        }
      } else if (key === table) {
        entry.rows.push({ ...record });
      } else {
        // New row — insert into filtered caches whose filter it matches.
        // Without this, live views that queried with filters (e.g. project_assignments
        // scoped to a projectId) never see newly-saved rows and appear stale until reload.
        if (recordMatchesCacheKey(record, table, key)) {
          entry.rows.push({ ...record });
        }
      }
      entry.fetchedAt = now;
    }
  }
  if (!cache.has(table)) {
    cache.set(table, { rows: [{ ...record }], fetchedAt: now, opts });
  }
}

function recordMatchesCacheKey(record: any, table: string, key: string): boolean {
  // Cache key format: "table:[["camelKey","value"],...]"  (see cacheKey())
  const filterStr = key.slice(table.length + 1);
  if (!filterStr) return false;
  try {
    const filter = JSON.parse(filterStr) as [string, any][];
    return filter.every(([fk, fv]) => record[fk] === fv);
  } catch {
    return false;
  }
}

function removeCacheEntry(table: string, id: string) {
  const now = Date.now();
  for (const [key, entry] of cache.entries()) {
    if (key === table || key.startsWith(table + ':')) {
      entry.rows = entry.rows.filter((r: any) => r.id !== id);
      entry.fetchedAt = now;
    }
  }
}

// ─── Cache management ───

export function invalidate(table: string): void {
  for (const key of cache.keys()) {
    if (key === table || key.startsWith(table + ':')) {
      cache.delete(key);
    }
  }
}

export function invalidateAll(): void {
  for (const entry of cache.values()) {
    entry.fetchedAt = 0;
  }
}

export function clearAll(): void {
  cache.clear();
}

export function onDataChange(listener: (table: string) => void): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

export function getCached<T = any>(table: string, id: string): T | undefined {
  const entry = cache.get(table);
  if (!entry) return undefined;
  return entry.rows.find((r: any) => r.id === id) as T | undefined;
}

export function listCached<T = any>(table: string): T[] {
  const entry = cache.get(table);
  return entry ? (entry.rows as T[]) : [];
}

/** Apply a live change from WebSocket broadcast to the local cache */
export function applyRealtimeEvent(
  table: string,
  eventType: 'save' | 'remove' | 'update',
  data: any
): void {
  if (eventType === 'save' && data?.id) {
    // Convert snake_case DB row to camelCase for the cache
    const entry = cache.get(table);
    const opts = entry?.opts;
    const camelRow = rowToCamel({ ...data }, opts);
    updateCacheEntry(table, camelRow as any, opts);
    notifyChange(table);
  } else if (eventType === 'remove' && data?.id) {
    removeCacheEntry(table, data.id);
    notifyChange(table);
  } else if (eventType === 'update' && Array.isArray(data)) {
    const entry = cache.get(table);
    const opts = entry?.opts;
    for (const row of data) {
      const camelRow = rowToCamel({ ...row }, opts);
      updateCacheEntry(table, camelRow as any, opts);
    }
    notifyChange(table);
  }
}
